import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import ROUTES from '../../routes/routes';
import { MenuItem } from './blocks/menu-item';
import styles from './menu.module.scss';
import {SVGS} from '../../assets/images';
import {Box} from '@mui/material';
import Skeleton from '../shared/skeleton';
import {useAppSelector} from '../../hooks/hooks';
import {Part, Profile} from '../../features/profile/types';
import MediaQuery, {useMediaQuery} from 'react-responsive';
import classNames from 'classnames';
import HideButton from './blocks/hide-button';
import { ChildMenuItem } from './blocks/child-menu-item';
import DarkModeToggle from '../dark-mode-toggle';
import {useThemeContext} from '../../contexts/theme-context';
import {IRouteItem} from '../../routes/interfaces/IRouteItem';
import {useLayoutState} from '../../contexts/layout-context';
import {
    canSeeClientsSection,
    canSeeEmployeesSection,
    canSeeProjectsSection,
    canSeeRolesSection,
    canSeeScheduleSection,
    canSeeSettingsSection,
    canSeeTypesSection,
    canSeeVacationSection,
} from '../../features/profile/navigationVisibility';
import {
    canSeeCostReportPage,
    canSeeTimeBankReportPage,
    canSeeTimeReportPage,
    canSeeTimesheetPage,
} from '../../features/profile/entityPermissions';

export default function Menu(props: { loading: boolean }) {
    const profile = useAppSelector(store => store.profile);
    const [open, setOpen] = useState<boolean>(useMediaQuery({ minWidth: 1001 }));
    const isMobile = useMediaQuery({ maxWidth: 1000  });
    const {isDark, setTheme} = useThemeContext();
    const {
        setDesktopMenuOpen,
        setDesktopMenuPhase
    } = useLayoutState();
    const desktopTransitionFallbackRef = useRef<number | null>(null);

    const clearDesktopTransitionFallback = useCallback(() => {
        if (desktopTransitionFallbackRef.current === null || typeof window === 'undefined') {
            return;
        }

        window.clearTimeout(desktopTransitionFallbackRef.current);
        desktopTransitionFallbackRef.current = null;
    }, []);

    const scheduleDesktopTransitionFallback = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }

        clearDesktopTransitionFallback();
        desktopTransitionFallbackRef.current = window.setTimeout(() => {
            setDesktopMenuPhase('idle');
            desktopTransitionFallbackRef.current = null;
        }, 260);
    }, [clearDesktopTransitionFallback, setDesktopMenuPhase]);

    const getScheduleChildrenUrls = (currentProfile: Profile): string[] => {
        const childrenUrls: string[] = [];

        if (canSeeTimesheetPage(currentProfile)) {
            childrenUrls.push(ROUTES.timesheet.url!);
        }

        if (canSeeTimeReportPage(currentProfile)) {
            childrenUrls.push(ROUTES.timereport.url!);
        }

        if (canSeeTimeBankReportPage(currentProfile)) {
            childrenUrls.push(ROUTES.timebankreport.url!);
        }

        if (canSeeCostReportPage(currentProfile)) {
            childrenUrls.push(ROUTES.costreport.url!);
        }

        if (canSeeVacationSection(currentProfile)) {
            childrenUrls.push(ROUTES.vacationreport.url!);
        }

        return childrenUrls;
    };

    const scheduleChildrenUrls = useMemo(() => getScheduleChildrenUrls(profile), [profile]);

    const toggle = useCallback(() => {
        setOpen((prev) => {
            const next = !prev;

            if (!isMobile) {
                setDesktopMenuOpen(next);
                setDesktopMenuPhase(next ? 'opening' : 'closing');
                scheduleDesktopTransitionFallback();
            }

            return next;
        });
    }, [isMobile, scheduleDesktopTransitionFallback, setDesktopMenuOpen, setDesktopMenuPhase]);

    const closeMobileMenu = useCallback(() => {
        if (isMobile) {
            setOpen(false);
        }
    }, [isMobile]);

    const handleDesktopTransitionEnd = useCallback<React.TransitionEventHandler<HTMLDivElement>>((event) => {
        if (event.propertyName !== 'width') {
            return;
        }

        clearDesktopTransitionFallback();
        setDesktopMenuPhase('idle');
    }, [clearDesktopTransitionFallback, setDesktopMenuPhase]);

    useEffect(() => {
        if (!isMobile) {
            setDesktopMenuOpen(open);
            return;
        }

        setDesktopMenuPhase('idle');
    }, [isMobile, open, setDesktopMenuOpen, setDesktopMenuPhase]);

    useEffect(() => {
        return () => {
            clearDesktopTransitionFallback();
        };
    }, [clearDesktopTransitionFallback]);

    const handleMobileMenuItemClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(() => {
        closeMobileMenu();
    }, [closeMobileMenu]);

    const renderMenuItems = useCallback((options: {
        isMenuOpen: boolean;
        onItemClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    }) => {
        const {isMenuOpen, onItemClick} = options;
        const showTypes = canSeeTypesSection(profile);
        const showRoles = canSeeRolesSection(profile);
        const showEmployees = canSeeEmployeesSection(profile);
        const showClients = canSeeClientsSection(profile);
        const showProjects = canSeeProjectsSection(profile);
        const showTimesheetPage = canSeeTimesheetPage(profile);
        const showTimeReport = canSeeTimeReportPage(profile);
        const showTimeBankReport = canSeeTimeBankReportPage(profile);
        const showCost = canSeeCostReportPage(profile);
        const showVacation = canSeeVacationSection(profile);
        const showSchedule = canSeeScheduleSection(profile);
        const showSettings = canSeeSettingsSection(profile);
        const scheduleChildRoutes: IRouteItem[] = [];

        if (showTimesheetPage) {
            scheduleChildRoutes.push(ROUTES.timesheet);
        }

        if (showTimeReport) {
            scheduleChildRoutes.push(ROUTES.timereport);
        }

        if (showTimeBankReport) {
            scheduleChildRoutes.push(ROUTES.timebankreport);
        }

        if (showCost) {
            scheduleChildRoutes.push(ROUTES.costreport);
        }

        if (showVacation) {
            scheduleChildRoutes.push(ROUTES.vacationreport);
        }

        return (
            <>
                {showTypes && <MenuItem route={ROUTES.types} open={isMenuOpen} callback={onItemClick} />}
                {showRoles && <MenuItem route={ROUTES.roles} open={isMenuOpen} callback={onItemClick} />}
                {showEmployees && <MenuItem route={ROUTES.employees} open={isMenuOpen} callback={onItemClick} />}
                {showClients && <MenuItem route={ROUTES.clients} open={isMenuOpen} callback={onItemClick} />}
                {showProjects && <MenuItem route={ROUTES.projects} open={isMenuOpen} callback={onItemClick} />}
                {showSchedule && (
                    <MenuItem
                        route={ROUTES.schedule}
                        open={isMenuOpen}
                        callback={onItemClick}
                        childrenUrls={scheduleChildrenUrls}
                    >
                        {scheduleChildRoutes.map((childRoute) => (
                            <ChildMenuItem
                                key={childRoute.name}
                                route={childRoute}
                                open={isMenuOpen}
                                onClick={onItemClick}
                            />
                        ))}
                    </MenuItem>
                )}
                {showSettings && <MenuItem route={ROUTES.settings} open={isMenuOpen} callback={onItemClick} />}
                <MenuItem route={ROUTES.profile} open={isMenuOpen} callback={onItemClick} />
            </>
        )
    }, [profile, scheduleChildrenUrls]);

    return (<>
        {/* desktop */}    
        <MediaQuery minWidth={1001}>
            <div className={classNames({
                [styles.desktopMenu]: true,
                [styles.open]: open
            })} onTransitionEnd={handleDesktopTransitionEnd}>
                <div className={styles.menuWrapper}>
                    <div style={{ padding: '16px' }}>
                        { open
                            ? <SVGS.LogoIcon width={170} height={44} className={styles.logoIcon} />
                            : <SVGS.LogoJIcon width={44} height={44} fill='var(--icons-primary)' /> }
                    </div>
                    <div className={styles.menuItems}>
                        {
                            props.loading ? [...Array(Object.keys(Part).length % 3).keys()]
                                .map((_, index) => (
                                    <Skeleton key={index} variant={'rectangular'} width={'100%'} height={44} />
                                ))
                                : renderMenuItems({isMenuOpen: open})
                        }
                    </div>
                </div>
                <div className={styles.menuBottom}>
                    <DarkModeToggle
                        checked={isDark}
                        onChange={(nextIsDark) => setTheme(nextIsDark ? 'dark' : 'light')}
                        showLabel={open}
                    />
                    <HideButton open={open} toggle={toggle} />
                </div>
            </div>
        </MediaQuery>

        {/* mobile */}   
        <MediaQuery maxWidth={1000}>
            <div className={styles.mobileMenu}>
                <Box>
                    <SVGS.LogoMobileIcon fill='var(--icons-primary)' />
                </Box>
                <Box>
                    {!open ? <SVGS.MenuMobileIcon onClick={toggle} fill='var(--icons-primary)' /> : <SVGS.CrossIcon onClick={toggle} fill='var(--icons-primary)'/>}
                </Box>
            </div>
            {open && <Box className={styles.opened}>
                <div className={styles.openedMenuWrapper}>
                    {renderMenuItems({
                        isMenuOpen: true,
                        onItemClick: handleMobileMenuItemClick,
                    })}
                </div>
                <div className={styles.openedBottom}>
                    <div className={styles.mobileToggleWrapper}>
                        <DarkModeToggle
                            checked={isDark}
                            onChange={(nextIsDark) => setTheme(nextIsDark ? 'dark' : 'light')}
                            showLabel
                        />
                    </div>
                </div>
            </Box>}
        </MediaQuery>
    </>)
}
